import { testsConfig } from '@/ab-tests/config';
import type { Test, TestEnvironment, TestOptions } from '@/ab-tests/types';
import type { SiteId } from '@/features/basket/types/site';
import { isJSON } from 'validator';

export const getRandomValue = (options: TestOptions[]): TestOptions => {
  return options[Math.floor(Math.random() * options.length)];
};

export const runTestCallbacks = (tests: Test[], siteId: SiteId) => {
  testsConfig.forEach(({ enabled, siteIds, name, callback, environment }) => {
    const findTest = tests.find((test) => test.name === name);
    if (!findTest) return;

    if (isTestEnabled(enabled, siteId, siteIds, environment) && callback) {
      callback(findTest.value);
    }
  });
};

export const updateExistingTests = (
  storedTestValue: string,
  siteId: SiteId,
): Test[] => {
  const cookieValue = JSON.parse(storedTestValue) as Test[];

  return testsConfig
    .map(({ enabled, siteIds, name, defaultValue: value, environment }) => {
      if (!isTestEnabled(enabled, siteId, siteIds, environment)) return null;

      const existingTest = cookieValue.find((test) => test.name === name);

      return existingTest ? existingTest : { name, value };
    })
    .filter((test): test is Test => test !== null);
};

export const generateNewTests = (siteId: SiteId) => {
  return testsConfig
    .map(({ enabled, options, siteIds, name, environment }) => {
      if (!isTestEnabled(enabled, siteId, siteIds, environment)) return null;

      return {
        name,
        value: getRandomValue(options),
      };
    })
    .filter((test): test is Test => test !== null);
};

export const isTestEnabled = (
  enabled: boolean,
  siteId: SiteId,
  enabledSiteIds?: SiteId[],
  environment?: TestEnvironment,
): boolean => {
  if (!enabled) return false;
  if (enabledSiteIds && !enabledSiteIds.includes(siteId)) return false;
  const currentEnvironment = process.env.NEXT_PUBLIC_APP_ENV as TestEnvironment;

  return !(environment && currentEnvironment !== environment);
};

export const mapCookiesToHeadersValue = (
  cookieValue: string | undefined,
  siteId: SiteId,
): string => {
  if (!cookieValue || !isJSON(cookieValue)) return '';
  const tests = JSON.parse(cookieValue) as Test[];

  const testHeaders = tests.reduce(
    (acc, test) => {
      const testConfig = testsConfig.find(({ name }) => name === test.name);
      if (!testConfig) return acc;
      if (
        !isTestEnabled(
          testConfig.enabled,
          siteId,
          testConfig.siteIds,
          testConfig.environment,
        )
      )
        return acc;

      acc[test.name] = test.value;

      return acc;
    },
    {} as Record<string, TestOptions>,
  );

  return JSON.stringify(testHeaders);
};

export const updateTestsWithSearchParams = (
  searchParams: URLSearchParams,
  testValues: Test[],
) => {
  const searchAbTestName = searchParams.get('test_name')?.toLowerCase();
  const searchAbTestValue = searchParams.get('test_value')?.toLowerCase();

  if (!searchAbTestName || !searchAbTestValue) return testValues;

  const testConfig = testsConfig.find(
    (test) => test.name.toLowerCase() === searchAbTestName,
  );

  if (!testConfig) return testValues;

  return testValues.map((test) => {
    if (
      test.name.toLowerCase() === searchAbTestName &&
      testConfig.options.includes(searchAbTestValue as Test['value'])
    ) {
      return {
        ...test,
        value: searchAbTestValue as TestOptions,
      };
    } else {
      return test;
    }
  });
};
